'use client';

import { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import { useHandleError } from '@/hooks/useHandleError';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { Medal } from '@/types/achievements';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import Header from '@/components/modal/Header';

export default function DashboardAchievementMedal() {
    const [isProcessing, setIsProcessing] = useState(true);
    const [medals, setMedals] = useState<Medal[]>([]);
    const [totalAchievementScore, setTotalAchievementScore] = useState(0);
    const [selectedSignatureMedalCd, setSelectedSignatureMedalCd] = useState('');
    const { handleError } = useHandleError();
    const setUserMedal = useAuthStore((s) => s.setUserMedal);

    useEffect(() => {
        (async () => {
            try {
                const data = await apiFetch('/dashboard/my_achievement/medals');
                setMedals(data);
                const signatureMedal = data.find((medal: Medal) => medal.signature === 1);
                if (signatureMedal) {
                    const initialMedalCd = signatureMedal.medal_cd;
                    setSelectedSignatureMedalCd(initialMedalCd);
                }
                const totalScore = data.reduce((acc: number, medal: Medal) => acc + (medal.achievement_score || 0), 0);
                setTotalAchievementScore(totalScore);
            } catch (error: unknown) {
                handleError(error);
            } finally {
                setIsProcessing(false);
            }
        })();
    }, []);

    const handleMedalClick = async (medalCd: string) => {
        try {
            const signatureMedal = await apiFetch('/dashboard/my_achievement/medals/signature', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ signature_medal_cd: medalCd }),
            });
            setSelectedSignatureMedalCd(medalCd);
            setUserMedal({
                medalCd: medalCd,
                achievementScore: signatureMedal.achievement_score,
                userSignatureMedalUrl: signatureMedal.medal_resource_url,
                medalName: signatureMedal.medal_name,
            });
        } catch (error: unknown) {
            handleError(error);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleMedalChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        handleMedalClick(e.target.value);
    };

    return (
        <>
            <div className="w-full h-full p-2 overflow-y-scroll">
                <div className="flex flex-col justify-start items-start w-full h-full p-2">
                    <div className="flex items-center w-full h-12">
                        <Header title="COLLECTION" subTitle="" />
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 w-full bg-color-co-gray-light rounded-xl px-4 py-3 mt-1">
                        <div className="flex items-center">
                            <span className="font-corp font-bold text-sm text-color-base">獲得ムサコレメダル</span>
                            <span className="font-montserrat font-bold text-2xl lg:text-3xl text-color-c-red px-2 lg:px-3">
                                {medals.length}
                            </span>
                            <span className="font-corp font-bold text-sm text-color-base">コ</span>
                        </div>
                        <div className="flex items-center">
                            <span className="font-corp font-bold text-sm text-color-base">ムサコレメダルスコア</span>
                            <span className="font-montserrat font-bold text-2xl lg:text-3xl text-color-c-orange pl-2 lg:pl-3">
                                {totalAchievementScore}
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center w-full font-notojp font-medium text-xs text-color-co-gray-dark px-1 pt-2.5 pb-4 leading-relaxed">
                        <IconAdaptive
                            Icon={Info}
                            strokeWidth={2.0}
                            baseSize={16}
                            breakpoints={{ md: 16 }}
                            className="mr-[1px] text-gray-500"
                        />
                        <p className="pl-1 pb-[1px]">ムサコレメダルは称号として１つセットできます。</p>
                    </div>
                    <div className="flex flex-col items-center w-full">
                        {medals.length === 0 ? (
                            <div className="flex flex-col w-full h-full font-corp font-medium text-sm lg:text-base text-gray-500 text-center pb-20">
                                <div className="grid gap-1 items-stretch w-full pt-32 text-color-base">
                                    獲得したムサコレはありません
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col w-full pb-8">
                                {medals.map((medal, index) => {
                                    const isSelected = selectedSignatureMedalCd === medal.medal_cd;
                                    return (
                                        <div
                                            key={index}
                                            onClick={() => handleMedalClick(medal.medal_cd)}
                                            className={`relative flex flex-col lg:flex-row items-center gap-3 lg:gap-6 w-full cursor-pointer rounded-2xl bg-color-white-base pt-4 pr-4 pb-9 pl-4 lg:pb-4 lg:pl-16 mb-3 ${
                                                isSelected
                                                    ? 'border-2 border-color-dashboard-signature-achievement-selected'
                                                    : ''
                                            }`}
                                            style={isSelected ? undefined : { border: '2px solid rgba(0, 0, 0, 0.12)' }}
                                        >
                                            <div className="absolute top-3 left-3 lg:top-1/2 lg:-translate-y-1/2 lg:left-3">
                                                <label
                                                    className="relative flex cursor-pointer items-center rounded-full"
                                                    htmlFor={`select_signature_medal_${medal.medal_cd}`}
                                                    data-ripple-dark="true"
                                                >
                                                    <input
                                                        checked={isSelected}
                                                        id={`select_signature_medal_${medal.medal_cd}`}
                                                        type="radio"
                                                        value={medal.medal_cd}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={handleMedalChange}
                                                        className="before:content[''] peer relative h-8 w-8 cursor-pointer appearance-none rounded-full
                                                            border-2 border-gray-300 text-color-dashboard-signature-achievement-selected transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity
                                                            checked:border-color-dashboard-signature-achievement-selected checked:before:bg-color-dashboard-signature-achievement-selected hover:before:opacity-10"
                                                    />
                                                    <div className="pointer-events-none absolute top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 opacity-0 transition-opacity peer-checked:opacity-100">
                                                        <ImageAdaptive
                                                            src="/images/common/radio-select.svg"
                                                            baseWidth={18}
                                                        />
                                                    </div>
                                                </label>
                                            </div>

                                            <div className="flex justify-center items-center flex-none">
                                                <img
                                                    src={medal.medal_resource_url}
                                                    className="w-[150px] lg:w-[180px]"
                                                />
                                            </div>

                                            <div className="flex flex-col flex-grow w-full">
                                                <div className="flex items-center gap-3">
                                                    <ImageAdaptive
                                                        src={`/images/achievement/medal/medal-${medal?.achievement_score}.svg`}
                                                        baseWidth={44}
                                                    />
                                                    <div className="flex flex-col">
                                                        <div className="font-corp font-bold text-base lg:text-xl text-color-base">
                                                            {medal?.medal_name}
                                                        </div>
                                                        <div className="font-notojp font-medium text-xs text-color-base pt-1">
                                                            {medal?.description}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center w-full pt-3">
                                                    <div className="flex items-center rounded-full border border-color-co-gray-light px-3 py-1">
                                                        <span className="font-corp font-bold text-color-base text-xs">
                                                            スコア
                                                        </span>
                                                        <span className="font-montserrat font-medium text-base lg:text-lg text-color-c-orange pl-3">
                                                            {medal?.achievement_score}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="absolute bottom-3 right-4 font-montserrat font-medium text-[10px] lg:text-xs tracking-widest text-color-co-gray-dark">
                                                {medal?.award_date_time}
                                            </div>
                                        </div>
                                    );
                                })}{' '}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
